import type { NavItem, UserMenuItem } from '@/config/navigation'
import { useMemo } from 'react'

import type { FeatureFlagKeyType, SectionKey } from '@scrambl/shared'
import {
  AccountType,
  ModuleTypeEnum,
  PermissionActionEnum,
} from '@scrambl/shared'

import { createLocalIcon } from '../components/local-icon'
import { useAccountType } from './useAccountType'
import { useEntityTerminology } from './useEntityTerminology'
import { useIsExternalUser } from './useIsExternalUser'
import { usePermissions } from './usePermissions'
import { useSupplierTerminology } from './useSupplierTerminology'

interface NavSection {
  key: SectionKey
  title?: string
  items: NavItem[]
  order: number
}

export function useNavigationItems() {
  const { Plural: EntitiesPlural } = useEntityTerminology()
  const { Plural: SuppliersPlural } = useSupplierTerminology()
  const { checkPermission } = usePermissions()
  const isExternalUser = useIsExternalUser()
  const { isPersonal } = useAccountType()
  const canReadExpenses = checkPermission(
    ModuleTypeEnum.EXPENSE,
    PermissionActionEnum.READ,
  )
  const canApproveExpenses = checkPermission(
    ModuleTypeEnum.EXPENSE,
    PermissionActionEnum.APPROVE,
  )
  const canManageExpenses = checkPermission(
    ModuleTypeEnum.EXPENSE,
    PermissionActionEnum.MANAGE,
  )
  // Same rule as pages/expenses: read, approve or manage opens the finance
  // Expenses module; everyone else logs expenses under My Time.
  const canAccessFinanceExpenses =
    canReadExpenses || canApproveExpenses || canManageExpenses
  const canApproveTimesheets = checkPermission(
    ModuleTypeEnum.TIME_ENTRY,
    PermissionActionEnum.APPROVE,
  )
  const canManageTimesheets = checkPermission(
    ModuleTypeEnum.TIME_ENTRY,
    PermissionActionEnum.MANAGE,
  )
  // Operations > Timesheets is the org-wide approvals surface; it matches the
  // review gate the approvals tab itself uses (MANAGE || APPROVE). Members on
  // the Edit tier keep Time > My Timesheets.
  const canAccessTimesheets = canApproveTimesheets || canManageTimesheets

  const navSections = useMemo<NavSection[]>(() => {
    const baseSections: NavSection[] = [
      {
        key: 'main',
        title: 'You',
        order: 1,
        items: [
          {
            href: '/today',
            label: 'Overview',
            icon: createLocalIcon('home-outline'),
            role: 'all',
          },
          {
            href: '/tasks/mine',
            label: isPersonal ? 'My Day' : 'My Work',
            icon: createLocalIcon('checkbox-outline'),
            role: 'all',
            permission: {
              module: ModuleTypeEnum.TASK,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/notes/mine',
            label: 'Notes',
            icon: createLocalIcon('sticky-note-outline'),
            role: 'all',
            featureFlag: 'NOTES' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.NOTE,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/my-time',
            label: 'Time',
            icon: createLocalIcon('time-outline'),
            role: 'all',
            featureFlag: 'TIME_TRACKING' as FeatureFlagKeyType,
          },
          {
            href: '/calendar',
            label: 'Calendar',
            icon: createLocalIcon('calendar-outline'),
            role: 'all',
            featureFlag: 'CALENDAR' as FeatureFlagKeyType,
          },
        ],
      },
      {
        key: 'work',
        title: 'Workspace',
        order: 2,
        items: [
          {
            href: '/my-organization',
            label: isPersonal ? 'Home Base' : 'My Organization',
            icon: createLocalIcon('organization-outline'),
            role: 'all',
            permission: {
              module: ModuleTypeEnum.ORGANIZATION,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/projects',
            label: 'Projects',
            icon: createLocalIcon('folder-outline'),
            role: 'all',
            featureFlag: 'PROJECTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.PROJECT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/tasks',
            label: 'Tasks',
            icon: createLocalIcon('checkbox-outline'),
            role: 'all',
            featureFlag: 'TASKS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.TASK,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/tickets',
            label: 'Tickets',
            icon: createLocalIcon('ticket-outline'),
            role: 'all',
            featureFlag: 'TICKETS' as FeatureFlagKeyType,
            subscriptionFeature: 'tickets',
            permission: {
              module: ModuleTypeEnum.TICKET,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/knowledge-base',
            label: 'Knowledge Base',
            icon: createLocalIcon('document-text-outline'),
            role: 'all',
            featureFlag: 'KNOWLEDGE_BASE' as FeatureFlagKeyType,
            subscriptionFeature: 'knowledge_base',
            permission: {
              module: ModuleTypeEnum.KNOWLEDGE_BASE,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/collections',
            label: 'Collections',
            icon: createLocalIcon('list-outline'),
            role: 'all',
            featureFlag: 'COLLECTIONS' as FeatureFlagKeyType,
            subscriptionFeature: 'collections',
            permission: {
              module: ModuleTypeEnum.CUSTOM_COLLECTION,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/forms',
            label: 'Forms',
            icon: createLocalIcon('clipboard-text-outline'),
            role: 'all',
            featureFlag: 'FORMS' as FeatureFlagKeyType,
            subscriptionFeature: 'forms',
            permission: {
              module: ModuleTypeEnum.FORM,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/threads',
            label: 'Discussions',
            icon: createLocalIcon('chatbubble-outline'),
            role: 'all',
            featureFlag: 'DISCUSSIONS' as FeatureFlagKeyType,
            subscriptionFeature: 'discussions',
            permission: {
              module: ModuleTypeEnum.THREAD,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/documents',
            label: 'Documents',
            icon: createLocalIcon('document-text-outline'),
            role: 'all',
            featureFlag: 'DOCUMENTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.DOCUMENT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/events',
            label: 'Events',
            icon: createLocalIcon('calendar-outline'),
            role: 'all',
            featureFlag: 'EVENTS' as FeatureFlagKeyType,
            subscriptionFeature: 'events',
            permission: {
              module: ModuleTypeEnum.EVENT,
              action: PermissionActionEnum.READ,
            },
          },
        ],
      },
      {
        key: 'sales',
        title: 'Sales',
        order: 4,
        items: [
          // Contacts is the single CRM person surface. Leads are a saved view
          // inside Contacts, not a separate destination.
          {
            href: '/contacts',
            label: isPersonal ? 'Address Book' : 'Contacts',
            icon: createLocalIcon('person-outline'),
            role: 'all',
            featureFlag: 'CONTACTS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.CONTACT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/deals',
            label: 'Deals',
            icon: createLocalIcon('deal-outline'),
            role: 'marketing',
            featureFlag: 'DEALS' as FeatureFlagKeyType,
            subscriptionFeature: 'deals',
            permission: {
              module: ModuleTypeEnum.DEAL,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/marketing',
            label: 'Marketing',
            icon: createLocalIcon('megaphone-outline'),
            role: 'marketing',
            featureFlag: 'MARKETING' as FeatureFlagKeyType,
            subscriptionFeature: 'marketing',
          },
          {
            href: '/scheduling',
            label: 'Scheduling',
            icon: createLocalIcon('calendar-outline'),
            role: 'all',
            featureFlag: 'SCHEDULING' as FeatureFlagKeyType,
            subscriptionFeature: 'scheduling',
          },
          {
            href: '/live-chat',
            label: 'Live chat',
            icon: createLocalIcon('chatbubble-outline'),
            role: 'marketing',
            featureFlag: 'LIVE_CHAT' as FeatureFlagKeyType,
            subscriptionFeature: 'live_chat',
          },
          {
            href: '/quotes',
            label: 'Quotes',
            icon: createLocalIcon('receipt-outline'),
            role: 'marketing',
            featureFlag: 'QUOTES' as FeatureFlagKeyType,
            subscriptionFeature: 'quotes',
            permission: {
              module: ModuleTypeEnum.QUOTE,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/products',
            label: 'Products',
            icon: createLocalIcon('cube-outline'),
            role: 'marketing',
            featureFlag: 'PRODUCTS' as FeatureFlagKeyType,
            subscriptionFeature: 'products',
            // Products reuse the QUOTE permission category (price book
            // grew out of Quotes).
            permission: {
              module: ModuleTypeEnum.QUOTE,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/communications',
            label: 'Communications',
            icon: createLocalIcon('call-outline'),
            role: 'marketing',
            featureFlag: 'CALLING' as FeatureFlagKeyType,
            subscriptionFeature: 'calling',
          },
        ],
      },
      {
        key: 'operations',
        title: 'Operations',
        order: 3,
        items: [
          {
            href: '/entities',
            label: EntitiesPlural,
            icon: createLocalIcon('building-outline'),
            role: 'all',
            featureFlag: 'ENTITIES' as FeatureFlagKeyType,
            subscriptionFeature: 'clients',
            permission: {
              module: ModuleTypeEnum.ENTITY,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/suppliers',
            label: SuppliersPlural,
            icon: createLocalIcon('building-outline'),
            role: 'all',
            featureFlag: 'SUPPLIERS' as FeatureFlagKeyType,
            subscriptionFeature: 'suppliers',
            permission: {
              module: ModuleTypeEnum.SUPPLIER,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/team',
            label: 'Team',
            icon: createLocalIcon('people-outline'),
            role: 'all',
            featureFlag: 'TEAM' as FeatureFlagKeyType,
            subscriptionFeature: 'team',
            permission: {
              module: ModuleTypeEnum.TEAM,
              action: PermissionActionEnum.READ,
            },
          },
          ...(canAccessTimesheets
            ? [
                {
                  href: '/timesheets',
                  label: 'Timesheets',
                  icon: createLocalIcon('time-outline'),
                  role: 'all' as const,
                  featureFlag: 'TIME_TRACKING' as FeatureFlagKeyType,
                },
              ]
            : []),
          {
            href: '/workflows',
            label: 'Workflows',
            icon: createLocalIcon('link-outline'),
            role: 'all',
            featureFlag: 'WORKFLOWS' as FeatureFlagKeyType,
            subscriptionFeature: 'workflows',
            permission: {
              module: ModuleTypeEnum.WORKFLOW,
              action: PermissionActionEnum.READ,
            },
          },
          {
            // Analytics: dashboards, saved reports, and alert rules. The
            // subscription key is `analytics` (open from Core, metered by
            // MAX_REPORTS / MAX_DASHBOARDS), NOT `advanced_reports` — that
            // one is Business-only and would hide the module from Core and
            // Pro orgs whose API happily serves it (`@RequireFeature
            // ('analytics')` on AnalyticsController).
            href: '/analytics',
            label: 'Analytics',
            icon: createLocalIcon('trending-up-outline'),
            role: 'all',
            featureFlag: 'ADVANCED_REPORTS' as FeatureFlagKeyType,
            subscriptionFeature: 'analytics',
            permission: {
              module: ModuleTypeEnum.ANALYTICS,
              action: PermissionActionEnum.READ,
            },
            // Business-only surface. The flag profile turns ADVANCED_REPORTS
            // off for new personal orgs; this covers orgs converted before
            // the flag existed, and a personal owner passes every permission
            // check so the permission alone would not keep it out.
            accountTypes: [AccountType.ORGANIZATION],
          },
        ],
      },
      {
        key: 'finance',
        title: 'Finance',
        order: 5,
        items: [
          {
            // Profitability module (internal name: financials) — org P&L,
            // forecast, scenarios, rates, earnings, moved out of Team.
            // FINANCIALS permission grants arrive via the permissions
            // backfill; org admins bypass permission checks either way.
            href: '/financials',
            label: 'Profitability',
            icon: createLocalIcon('trending-up-outline'),
            role: 'all',
            featureFlag: 'FINANCIALS' as FeatureFlagKeyType,
            permission: {
              module: ModuleTypeEnum.FINANCIALS,
              action: PermissionActionEnum.READ,
            },
            // Personal accounts have no P&L; the flag profile covers new
            // personal orgs, the account-type gate covers existing ones.
            accountTypes: [AccountType.ORGANIZATION],
            subscriptionFeature: 'profitability',
          },
          {
            href: '/contracts',
            label: 'Contracts',
            icon: createLocalIcon('contract-outline'),
            role: 'all',
            featureFlag: 'CONTRACTS' as FeatureFlagKeyType,
            subscriptionFeature: 'contracts',
            permission: {
              module: ModuleTypeEnum.CONTRACT,
              action: PermissionActionEnum.READ,
            },
          },
          {
            href: '/invoices',
            label: 'Invoices',
            icon: createLocalIcon('receipt-outline'),
            role: 'all',
            featureFlag: 'INVOICES' as FeatureFlagKeyType,
            subscriptionFeature: 'invoices',
            permission: {
              module: ModuleTypeEnum.INVOICE,
              action: PermissionActionEnum.READ,
            },
          },
          ...(canAccessFinanceExpenses
            ? [
                {
                  href: '/expenses',
                  label: 'Expenses',
                  icon: createLocalIcon('expense-outline'),
                  role: 'all' as const,
                  // Expenses is a business surface (TIME_TRACKING off for
                  // personal), but the org owner passes the expense permission
                  // check, so gate by account type to keep it out of personal.
                  accountTypes: [AccountType.ORGANIZATION],
                  subscriptionFeature: 'expenses',
                },
              ]
            : []),
        ],
      },
    ]
    if (isPersonal) {
      // Personal accounts collapse the Sales + Operations groups into a single
      // "People & Home" section holding the actual workspace members, address
      // book (Contacts), and household vendors (Suppliers). The Entities module
      // is business-only and is not repurposed for personal accounts. The
      // remaining Sales/Operations/Finance items are business-only and already
      // flag- or account-type-gated, so they don't need their own headers.
      const findItem = (href: string): NavItem | undefined =>
        baseSections
          .flatMap(section => section.items)
          .find(item => item.href === href)

      const peopleHomeItems = [
        {
          href: '/team',
          label: 'Members',
          icon: createLocalIcon('people-outline'),
          role: 'all' as const,
          featureFlag: 'TEAM' as FeatureFlagKeyType,
          permission: {
            module: ModuleTypeEnum.MEMBER,
            action: PermissionActionEnum.READ,
          },
        },
        findItem('/contacts'),
        findItem('/suppliers'),
      ].filter((item): item is NavItem => Boolean(item))

      const mainSection = baseSections.find(section => section.key === 'main')
      const workSection = baseSections.find(section => section.key === 'work')

      return [
        ...(mainSection ? [mainSection] : []),
        ...(workSection ? [workSection] : []),
        {
          key: 'operations',
          title: 'People & Home',
          order: 3,
          items: peopleHomeItems,
        },
      ]
    }
    if (isExternalUser) {
      // Hide only member-owned personal surfaces for external users.
      const externalDeniedMyRoutes = new Set(['/my-time'])
      return baseSections.map(section => ({
        ...section,
        items: section.items.filter(item => {
          if (!item.href?.startsWith('/my-')) return true
          return !externalDeniedMyRoutes.has(item.href)
        }),
      }))
    }
    return baseSections
  }, [
    EntitiesPlural,
    SuppliersPlural,
    canAccessFinanceExpenses,
    canAccessTimesheets,
    isExternalUser,
    isPersonal,
  ])

  // Add section information to each item
  const mainNavItems: NavItem[] = useMemo(
    () =>
      navSections
        .sort((a, b) => a.order - b.order)
        .flatMap(section =>
          section.items.map(item => ({
            ...item,
            section: section.key,
          })),
        ),
    [navSections],
  )

  // Single entry: the /user-settings page carries the global Account tab plus
  // the org-scoped Profile / Skills / Notifications / Integrations tabs.
  // UserMenu resolves the href per context (org, portal, or org-less /account).
  const userMenuItems: UserMenuItem[] = [
    {
      href: '/user-settings',
      label: 'Account settings',
      icon: createLocalIcon('person-circle-outline'),
      divider: true,
    },
  ]

  // Admin navigation items
  const adminNavItems: NavItem[] = [
    {
      href: '/admin/members',
      label: 'Members',
      icon: createLocalIcon('people-outline'),
    },
    {
      href: '/admin/integrations',
      label: 'Integrations',
      icon: createLocalIcon('grid-outline'),
    },
    {
      href: '/admin/settings',
      label: 'Settings',
      icon: createLocalIcon('settings-outline'),
    },
    {
      href: '/admin/logs',
      label: 'Audit Trail',
      icon: createLocalIcon('time-outline'),
      // Owner/admin only (OrgRouteGuard gates the /admin area), plan-gated on
      // the same subscription feature the controller requires. Personal
      // accounts never see it: the subscription layer is inert for them, so
      // the account type is the real gate.
      accountTypes: [AccountType.ORGANIZATION],
      subscriptionFeature: 'audit_log',
    },
    {
      href: '/admin/archive',
      label: 'Archive',
      // No archive glyph in the local set; the box reads as "put away".
      icon: createLocalIcon('cube-outline'),
      // Owner/admin only via OrgRouteGuard, matching the controller's
      // @Access(ORGANIZATION, OWNER, ADMIN). Work-only by default per the
      // account-separation contract; a personal space would also need the
      // Family / People / Address Book vocabulary before this reads correctly.
      accountTypes: [AccountType.ORGANIZATION],
    },
    {
      href: '/admin/subscription',
      label: 'Subscription & Billing',
      icon: createLocalIcon('receipt-outline'),
      // Personal accounts are free by construction — they have no subscription
      // flag row, so billing surfaces must be gated by account type, not flags.
      accountTypes: [AccountType.ORGANIZATION],
    },
  ]

  // Export sections configuration for use in other components
  const sections = navSections.reduce(
    (acc, section) => ({
      ...acc,
      [section.key]: {
        key: section.key,
        title: section.title,
        order: section.order,
      },
    }),
    {} as Record<
      SectionKey,
      { key: SectionKey; title?: string; order: number }
    >,
  )

  return {
    mainNavItems,
    userMenuItems,
    adminNavItems,
    sections,
  }
}
